import React, { useState, useEffect, useRef } from 'react';
import AppIcon from '/favicon.ico'; // Ensure the path is correct
import { useI18n } from '@src/i18n/I18nContext'; // Import i18n context
import { detectBrowser } from '@src/utils/browser-detection'; // Import browser detection utility
import ChevronRightIcon from '@src/assets/svg/chevronright.svg';

// Add this style block to the component
const cursorStyle = `
  .ti-cursor {
    margin-left: 8px !important;
  }
  
  .intro-page-exit {
    animation: fadeOut 0.5s ease-in-out forwards;
    pointer-events: none;
  }
  
  @keyframes fadeOut {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-20px); }
  }

  .intro-page-loading {
    animation: fadeIn 0.3s ease-out forwards;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    .ti-cursor {
      margin-left: 4px !important;
    }
  }

  /* Ensure full viewport coverage, eliminate white gaps */
  .intro-page-container {
    margin: 0;
    padding: 0;
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const IntroPage = ({ onTryNow, visible = true }: { onTryNow: () => void, visible?: boolean }) => {
  const { t, language } = useI18n(); // Use i18n hook
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [loadedImages, setLoadedImages] = useState<string[]>([]);
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [currentBrowser, setCurrentBrowser] = useState<string>('firefox');
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Video URL
  const videoUrl = 'https://ik.imagekit.io/tqaevu6rl/video/v.mp4/ik-video.mp4?updatedAt=1744807258817';
  
  // Screenshot URL list - Add ImageKit optimization parameters
  const screenshotUrls = [
    'https://ik.imagekit.io/tabby/r/main.png?updatedAt=1752333992990',
    'https://ik.imagekit.io/tabby/r/diy.png?updatedAt=1752333992913',
    'https://ik.imagekit.io/tabby/r/more.png?updatedAt=1752333992758',
    'https://ik.imagekit.io/tabby/r/diy.png?updatedAt=1752333992913'
  ];

  // Extension store links
  const storeLinks = {
    chrome: 'https://chrome.google.com/webstore/detail/nnbdkcldneflbihbandncaapjpjkbmca',
    edge: 'https://microsoftedge.microsoft.com/addons/detail/tabby-ai-new-tab/cphnfdibihgkaofllhejbpmhlkdpenpi',
    firefox: 'https://addons.mozilla.org/zh-CN/firefox/addon/tabby-ai-new-tab/',
  };

  // Detect browser type
  useEffect(() => {
    const browser = detectBrowser();
    
    if (browser === 'edge') {
      setCurrentBrowser('edge');
    } else if (browser === 'chrome') {
      setCurrentBrowser('chrome');
    } else {
      setCurrentBrowser('firefox'); // Default to firefox link
    }
  }, []);

  // Get current browser's store link
  const getStoreLink = () => {
    return storeLinks[currentBrowser as keyof typeof storeLinks] || storeLinks.firefox;
  };

  // Get button text
  const getButtonText = () => {
    switch (currentBrowser) {
      case 'chrome': return t('intro.add_to_chrome');
      case 'edge': return t('intro.add_to_edge') || 'Add to Edge';
      case 'firefox': return t('intro.add_to_firefox') || 'Add to Firefox';
      default: return t('intro.add_to_browser') || 'Add to Browser';
    }
  };

  // Optimized image preloading strategy
  useEffect(() => {
    // Only load the first image for initial display
    const loadFirstImage = () => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages([screenshotUrls[0]]);
        setIsFirstImageLoaded(true);
      };
      img.onerror = () => {
        console.error(`Failed to load first image: ${screenshotUrls[0]}`);
        setIsFirstImageLoaded(true);
      };
      img.src = screenshotUrls[0];
    };

    // Use Intersection Observer to lazy load other images
    const loadRemainingImages = () => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              const loadPromises = screenshotUrls.slice(1).map((url) => {
                return new Promise<string>((resolve, reject) => {
                  const img = new Image();
                  img.onload = () => resolve(url);
                  img.onerror = () => reject(url);
                  img.src = url;
                });
              });
              
              Promise.allSettled(loadPromises).then(results => {
                const successfullyLoadedImages = results
                  .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
                  .map(result => result.value);
                
                if (successfullyLoadedImages.length > 0) {
                  setLoadedImages(prev => [...prev, ...successfullyLoadedImages]);
                }
              });
            }, 500);
            observer.disconnect();
          }
        });
      }, { threshold: 0.1 });

      // Observe image container
      const imageContainer = document.querySelector('.intro-images');
      if (imageContainer) {
        observer.observe(imageContainer);
      }
    };

    // Delay loading video, lowest priority
    const loadVideo = () => {
      setTimeout(() => {
        if (!document.hidden) { // Only load video when page is visible
          const video = document.createElement('video');
          video.preload = 'none'; // Change to none, completely delay loading
          
          video.onloadedmetadata = () => {
            setIsVideoLoaded(true);
          };
          
          video.onerror = () => {
            console.error(`Failed to load video: ${videoUrl}`);
          };
          
          video.src = videoUrl;
        }
      }, 3000); // Delay video loading by 3 seconds
    };

    loadFirstImage();
    loadRemainingImages();
    loadVideo();
  }, []);

  // When video is loaded, switch to video display
  useEffect(() => {
    if (isVideoLoaded) {
      setShowVideo(true);
    }
  }, [isVideoLoaded]);

  // Carousel effect - only enabled when not showing video
  useEffect(() => {
    if (loadedImages.length === 0 || showVideo) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % loadedImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [loadedImages.length, showVideo]);

  const handleTryNow = () => {
    onTryNow();
  };

  if (!visible) return null;

  return (
    <div className={`intro-page-container z-50 overflow-auto intro-page-loading ${isExiting ? 'intro-page-exit' : ''}`}>
      <div className="w-full bg-gradient-to-br from-blue-100 to-purple-50">
        {/* Add style tag */}
        <style>{cursorStyle}</style>
        
        {/* Logo container - Remove top spacing */}
        <div className="px-6 md:px-16 pt-4 md:pt-8 mb-2 md:mb-24">
          <div className="flex items-center gap-3">
            <img src={AppIcon} alt="Tabby" className="w-16 h-16 md:w-20 md:h-20" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Tabby</h1>
          </div>
        </div>

        {/* Responsive container: Vertical stack on small screens, horizontal on large screens */}
        <div className="flex flex-col md:flex-row md:min-h-[60vh] px-6 md:px-16 py-4 md:py-0"> 
          <div className="md:flex-1 md:pr-10"> 
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-8 leading-tight">
              An elegant new tab,<br/>
              crafted for you
            </h1>
            <div className="text-lg md:text-xl text-gray-700 text-left max-w-3xl mb-10">
              <p>
              Tabby brings hand-picked wallpapers, custom backgrounds, and a built-in todo list to every new tab — lightweight and lightning fast.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-8 md:mb-0">
              <div className="dropdown dropdown-hover dropdown-end w-full sm:w-auto">
                <a 
                  href={getStoreLink()} 
                  className="btn btn-outline text-base md:text-lg hover:transform-none w-full text-black"
                  target="_blank"
                >
                  {getButtonText()}
                  <div 
                    className="badge ml-2 border-none" 
                    style={{ 
                      backgroundColor: '#A1C1FF',
                      color: '#000',
                      padding: '0.25rem 0.5rem'
                    }}
                  >
                    {t('intro.free')}
                  </div>
                </a>
                <ul className="dropdown-content menu p-2 shadow bg-base-100 rounded-box z-[1] w-full">
                  {currentBrowser !== 'chrome' && (
                    <li>
                      <a 
                        href={storeLinks.chrome} 
                        target="_blank"
                        className="flex items-center justify-between text-black"
                      >
                        <span>{t('intro.add_to_chrome') || 'Add to Chrome'}</span>
                        <div 
                          className="badge border-none" 
                          style={{ 
                            backgroundColor: '#A1C1FF',
                            color: '#000',
                            padding: '0.25rem 0.5rem'
                          }}
                        >
                          {t('intro.free')}
                        </div>
                      </a>
                    </li>
                  )}
                  {currentBrowser !== 'edge' && (
                    <li>
                      <a 
                        href={storeLinks.edge} 
                        target="_blank"
                        className="flex items-center justify-between text-black"
                      >
                        <span>{t('intro.add_to_edge') || 'Add to Edge'}</span>
                        <div 
                          className="badge border-none" 
                          style={{ 
                            backgroundColor: '#A1C1FF',
                            color: '#000',
                            padding: '0.25rem 0.5rem'
                          }}
                        >
                          {t('intro.free')}
                        </div>
                      </a>
                    </li>
                  )}
                  {currentBrowser !== 'firefox' && (
                    <li>
                      <a 
                        href={storeLinks.firefox} 
                        target="_blank"
                        className="flex items-center justify-between text-black"
                      >
                        <span>{t('intro.add_to_firefox') || 'Add to Firefox'}</span>
                        <div 
                          className="badge border-none" 
                          style={{ 
                            backgroundColor: '#A1C1FF',
                            color: '#000',
                            padding: '0.25rem 0.5rem'
                          }}
                        >
                          {t('intro.free')}
                        </div>
                      </a>
                    </li>
                  )}
                </ul>
              </div>
              <button 
                onClick={handleTryNow}
                className="btn btn-neutral text-base md:text-lg group relative overflow-visible w-full sm:w-auto"
              >
                <div className="flex items-center justify-center gap-2 transition-all duration-300 group-hover:translate-x-1">
                  {t('intro.try_now')}
                  <img 
                    src={ChevronRightIcon} 
                    className="w-5 h-5 transition-all duration-300 transform group-hover:translate-x-1"
                  />
                </div>
              </button>
            </div>
          </div>

          <div className="md:flex-1 relative md:pl-[6rem] flex items-center justify-center h-64 md:h-auto mt-0 md:mt-[-8rem] pb-8 md:pb-0 intro-images"> 
            {showVideo ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <video 
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full max-h-[300px] md:max-h-[500px] object-contain rounded-2xl shadow-2xl"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            ) : loadedImages.length > 0 ? (
              loadedImages.map((src, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 flex items-center justify-center ${
                    index === activeIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img 
                    src={src} 
                    alt={`${t('intro.screenshot')} ${index + 1}`}
                    className="w-full max-h-[300px] md:max-h-[500px] object-contain rounded-2xl shadow-2xl" 
                  />
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-gray-900"></div>
              </div>
            )}
          </div>
        </div>

        {/* What is Tabby Section */}
        <div className="px-6 md:px-16 py-12 md:py-16 bg-white/20 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-8">
              What is Tabby?
            </h2>
            <div className="text-lg md:text-xl text-gray-700 text-center max-w-3xl mx-auto mb-10">
              <p>
                Tabby is a lightweight, lightning-fast new tab extension. Beautiful wallpapers, custom backgrounds, smart bookmarks, and a built-in todo list — everything you need, nothing you don't.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {/* Lightning Fast */}
              <div className="bg-white/40 backdrop-blur-sm rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Lightning Fast</h3>
                <p className="text-gray-700">
                  Lightweight and instant — Tabby loads in milliseconds without slowing down your browser
                </p>
              </div>

              {/* Custom Wallpapers */}
              <div className="bg-white/40 backdrop-blur-sm rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Custom Wallpapers</h3>
                <p className="text-gray-700">
                  Upload your own images or pick from curated collections to make every new tab yours
                </p>
              </div>

              {/* Todo List */}
              <div className="bg-white/40 backdrop-blur-sm rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Todo List</h3>
                <p className="text-gray-700">
                  Track tasks right on your new tab and keep them in sync across your devices
                </p>
              </div>

              {/* Quick Access */}
              <div className="bg-white/40 backdrop-blur-sm rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Quick Access</h3>
                <p className="text-gray-700">
                  Open your favorite sites and search in one click, with smart bookmarks built in
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User Reviews Section */}
        <div className="px-6 md:px-16 py-12 md:py-16 pb-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-12">
              What Users Say
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Review 1 */}
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400 text-lg">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Beautiful wallpapers and it loads instantly — the custom
                  backgrounds make every new tab feel personal. Highly recommended!"
                </p>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold">Sarah M.</div>
                  <div>Chrome Web Store</div>
                </div>
              </div>

              {/* Review 2 */}
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400 text-lg">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Fast, clean, and gorgeous. The built-in todo list keeps me
                  organized all day, right from my new tab!"
                </p>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold">Emily R.</div>
                  <div>Designer</div>
                </div>
              </div>

              {/* Review 3 */}
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400 text-lg">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I set my favorite photo as the background and my new tab is
                  truly mine. Lightweight, beautiful, and a joy to use."
                </p>
                <div className="text-sm text-gray-600">
                  <div className="font-semibold">James T.</div>
                  <div>Product Manager</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="px-6 md:px-16 py-8 md:py-12 bg-white/10 backdrop-blur-sm border-t border-white/20">
          <div className="max-w-4xl mx-auto">
            {/* Logo */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="flex items-center gap-3 mb-4">
                <img src={AppIcon} alt="Tabby" className="w-12 h-12" />
                <span className="text-xl font-bold text-gray-800">Tabby</span>
              </div>
            </div>

            {/* Contact and Links */}
            <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 text-sm text-gray-600">
              <a 
                href="mailto:hi@tabbyio.com" 
                className="hover:text-blue-600 transition-colors"
              >
                Contact: hi@tabbyio.com
              </a>
              <span className="hidden md:inline text-gray-400">•</span>
              <a 
                href="https://tabbyio.com/privacy" 
                className="hover:text-blue-600 transition-colors"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              <span className="hidden md:inline text-gray-400">•</span>
              <a 
                href="https://tabbyio.com/terms" 
                className="hover:text-blue-600 transition-colors"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
            </div>

            {/* Copyright and Made with Love */}
            <div className="text-center mt-6 pt-4 border-t border-white/20">
              <p className="text-gray-600 mb-2">
                Made with ❤️ by <a href="https://tabbyio.com" className="text-blue-600 hover:text-blue-800 font-medium" target="_blank" rel="noopener noreferrer">TabbyIO</a>
              </p>
              <p className="text-xs text-gray-500">
                © 2025 TabbyIO. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroPage;